import React from 'react';
import { AppConfigs, AppCriteriaField, AppSkinProps } from '../../../types/decisions';
import { getFieldName, transformCriteriaObjectToArray } from '../../../utils';
import { Button } from '@mui/material';
import AppCriteriaTypeText from './CriteriaFields/AppCriteriaTypeText';
import AppCriteriaTypeNumber from './CriteriaFields/AppCriteriaTypeNumber';
import AppCriteriaTypeSelectV2 from './CriteriaFields/AppCriteriaTypeSelectV2';
import AppCriteriaTypeZip from './CriteriaFields/AppCriteriaTypeZip';
import AppCriteriaTypeGeo from './CriteriaFields/AppCriteriaTypeGeo';
import AppCriteriaTypeDate from './CriteriaFields/AppCriteriaTypeDate';
import AppCriteriaTypeColor from './CriteriaFields/AppCriteriaTypeColor';
import AppCriteriaTypeBool from './CriteriaFields/AppCriteriaTypeBool';
import CustomSettingsTitle from './CustomSelectFields/CustomSettingsTitle';
import CustomSelectSetting2 from './CustomSelectFields/CustomSelectSetting2';
import CustomSelectSetting4 from './CustomSelectFields/CustomSelectSetting4';
import { useAppConfig } from '../../../context/app-configs-context';
import NameWithIcon from './NameWithIcon/NameWithIcon';

interface Props {
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    fieldInitialValues: AppSkinProps['fieldInitialValues'];
    field: AppCriteriaField,
    areCTAsDisplayed?: boolean;
    handleModalClose?: () => void;
    displayFullWidth?: boolean;
    openCriteriaSettings?: (id: string) => void;
    disabled?: boolean;
    renderCustomSettings?: boolean;
    isInitialRun?: boolean;
    renderNewTitle?: boolean;
    highlightedOptionNames?: string[];
    revealedOptionNames?: string[];
    autoExpandedOptionValues?: Record<string, { from: number; to: number }>;
    onUserSettingsClose?: (fieldId: string) => void;
}

const ModalCTAs = ({ onClose }: { onSave: () => void; onClose: () => void; }) => {
    return (
        <div className="custom-modal-content-ctas" style={{ textAlign: 'right' }}>
            <Button
                variant="contained"
                onClick={onClose}
                style={{
                    marginLeft: 20,
                    minWidth: 120,
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: 'var(--app-primary, #2878d4)',
                }}
            >
                Close
            </Button>
        </div>
    );
};

const CriteriaSettingV2 = ({
    field,
    onSliderChange,
    onTradeoffValueChange,
    fieldInitialValues,
    areCTAsDisplayed = false,
    handleModalClose,
    displayFullWidth = false,
    openCriteriaSettings,
    disabled = false,
    renderCustomSettings = false,
    isInitialRun = false,
    renderNewTitle = false,
    highlightedOptionNames = [],
    revealedOptionNames,
    autoExpandedOptionValues,
    onUserSettingsClose,
}: Props) => {
    const { appConfigs: { criteriaSettings } } = useAppConfig();
    let settingContent: React.JSX.Element;
    let SelectComponent: typeof AppCriteriaTypeSelectV2 = AppCriteriaTypeSelectV2;

    if (renderCustomSettings) {
        if (field.type === 'select') {
            // @ts-ignore
            SelectComponent = CustomSelectSetting2;
        } else if (field.type === 'multiselect') {
            // @ts-ignore
            SelectComponent = CustomSelectSetting4;
        }

    }

    switch (field.type) {
        case 'text':
        case 'dynamic':
            const suggestedValues = field.suggestedValues?.length ? field.suggestedValues[0].val : undefined;
            settingContent = (
                <AppCriteriaTypeText
                    value={field.val as string}
                    hideImportance={true}
                    importance={+field.importance}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    criteriaConstraints={{}}
                    setCriteriaConstraints={() => {
                    }}
                    id={field.id}
                    disabled={disabled}
                    variant={field.matching}
                    suggestedValues={suggestedValues}
                />
            );
            break;
        case 'number':
            let numberValue: number | number[] = +field.val;
            if (field.style === 'range') {
                const rangeValues = field.val as unknown as string[] || [ '0', '0' ];
                numberValue = [ +rangeValues[0], +rangeValues[1] ];
            }

            settingContent = (
                <AppCriteriaTypeNumber
                    value={numberValue}
                    importance={+field.importance}
                    min={+field.min}
                    max={+field.max}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    criteriaConstraints={{}}
                    setCriteriaConstraints={() => {
                    }}
                    id={field.id}
                    variant={(
                        field.viewConfigs?.view || criteriaSettings.number
                    ) as AppConfigs['criteriaSettings']['number']}
                    isRange={field.style === 'range'}
                    disabled={disabled}
                />
            );
            break;
        case 'multiselect':
        case 'select':
            const fieldValue: any = fieldInitialValues.find(item => item.id === field.id)?.val;
            settingContent = (
                <SelectComponent
                    items={transformCriteriaObjectToArray(field.val, field.images, field.link)}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    initialValues={(
                        fieldValue || {}
                    ) as {
                        [key: string]: string
                    }}
                    criteriaConstraints={{}}
                    setCriteriaConstraints={() => {
                    }}
                    id={field.id}
                    variant={(
                        field.viewConfigs?.view || criteriaSettings.enumerated
                    ) as AppConfigs['criteriaSettings']['enumerated']}
                    isModal={areCTAsDisplayed}
                    onCriteriaSettingsOpen={openCriteriaSettings}
                    disabled={disabled}
                    isInitialRun={isInitialRun}
                    highlightedOptionNames={highlightedOptionNames}
                    {...(revealedOptionNames ? { revealedOptionNames, autoExpandedOptionValues } : {})}
                />
            );
            break;
        case 'bool':
            settingContent = (
                <AppCriteriaTypeBool
                    defaultChecked={field.val as unknown as boolean}
                    onChange={(newValue) => onTradeoffValueChange(newValue, field.id)}
                    trueLabel={field.labels?.true}
                    falseLabel={field.labels?.false}
                />
            );
            break;
        case 'zipcode':
            settingContent = (
                <AppCriteriaTypeZip
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    variant={(
                        field.viewConfigs?.view || criteriaSettings.zipcode
                    ) as AppConfigs['criteriaSettings']['zipcode']}
                    value={field.val as unknown as string[]}
                    disabled={disabled}
                />
            );
            break;
        case 'geoloc':
            settingContent = (
                <AppCriteriaTypeGeo
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    variant={(
                        field.viewConfigs?.view || criteriaSettings.longLat
                    ) as AppConfigs['criteriaSettings']['longLat']}
                    value={field.val}
                    displayFullWidth={displayFullWidth}
                    disabled={disabled}
                />
            );
            break;
        case 'date':
            settingContent = (
                <AppCriteriaTypeDate
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    value={field.val as string}
                    disabled={disabled}
                />
            );
            break;
        case 'color':
            settingContent = (
                <AppCriteriaTypeColor
                    id={field.id}
                    importance={+field.importance}
                    hideImportance={true}
                    onSliderChange={onSliderChange}
                    onTradeoffValueChange={onTradeoffValueChange}
                    value={field.val as unknown as {
                        color: string;
                        isSelected: boolean;
                    }[]}
                    variant={(
                        field.viewConfigs?.view || criteriaSettings.color
                    ) as AppConfigs['criteriaSettings']['color']}
                    disabled={disabled}
                />
            );
            break;
        default:
            settingContent = (
                <span>Unrecognized setting type.</span>
            );
            break;
    }

    const onCancel = () => {
        onUserSettingsClose?.(field.id);
        handleModalClose?.();
    };


    const onSave = () => {
        handleModalClose?.();
    };

    return (
        <div className="custom-modal-content criteria-modal-v2">
            <div key={field.id} className="results-app-settings-item">
                <div className="left">
                    {(renderNewTitle || areCTAsDisplayed) ? (
                        <CustomSettingsTitle
                            title={getFieldName(field.name)}
                            description={field.description}
                            link={field.link}
                            icon={field.icon}
                        />
                    ) : (
                        <div className="title">
                            <NameWithIcon icon={field.icon} name={getFieldName(field.name)} link={field.link}/>
                        </div>
                    )}
                </div>
                <div className="right" style={{ marginBottom: 16 }}>
                    {settingContent}
                </div>
                {areCTAsDisplayed ? <ModalCTAs onSave={onSave} onClose={onCancel}/> : null}
            </div>
        </div>
    );
};

export default CriteriaSettingV2;
